import { getApiBaseUrl } from "@/lib/api-base-url";
import { domGuardRepairs } from "@/lib/dom-guard";

/**
 * A page that crashed, told to the person and to us - `EX-SFX03`, ADR-0108.
 *
 * Before this, the error screen said "Something went wrong", hid the message in
 * production and sent nothing: a setter's eight `NotFoundError`s were known only
 * because they sent a screenshot. Now the screen names what happened, shows a
 * reference a person can read aloud, and posts a report the team finds by it
 * (`POST /v1/client-crashes`).
 *
 * The report carries what finds the bug and nothing that finds the person: the
 * error's name and message, the path without its query, the section that broke,
 * whether the page showed a translator's marks, the language and the build. It
 * is sent with no credentials, so no session rides along, and the server
 * refuses any field it does not know.
 */

export type CrashKind = "browser_changed_page" | "code";
export type CrashSurface = "page" | "section";

/** No 0, 1, I or O: a reference is read aloud and typed back. */
const REFERENCE_ALPHABET = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";

/** `P7K3-Q9M2`: eight characters from the browser's own random source. */
export function crashReference(random: (bytes: Uint8Array) => Uint8Array = defaultRandom): string {
  const bytes = random(new Uint8Array(8));
  const chars = Array.from(bytes, (byte) => REFERENCE_ALPHABET[byte % REFERENCE_ALPHABET.length]).join("");
  return `${chars.slice(0, 4)}-${chars.slice(4)}`;
}

function defaultRandom(bytes: Uint8Array): Uint8Array {
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    return crypto.getRandomValues(bytes);
  }
  for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  return bytes;
}

/**
 * Whether the page shows a translator's or an extension's hand: Google
 * Translate marks `<html>` and wraps text in `<font>`, Microsoft's translator
 * stamps `_msttexthash`, and the DOM guard counts every time it had to step
 * around a node somebody else moved (`lib/dom-guard.ts`).
 */
export function pageShowsTranslation(doc: Document | undefined = globalThis.document): boolean {
  if (!doc) return false;
  const root = doc.documentElement;
  if (root?.classList.contains("translated-ltr") || root?.classList.contains("translated-rtl")) return true;
  if (domGuardRepairs() > 0) return true;
  try {
    return Boolean(doc.querySelector('font[style*="vertical-align: inherit"], [_msttexthash]'));
  } catch {
    return false;
  }
}

/**
 * The two stories the error screen tells. React's DOM errors (`insertBefore`,
 * `removeChild`, "not a child of this node") are what a page changed under it
 * produces; on a page that shows a translator's marks, any `NotFoundError` is
 * read the same way. Everything else is ours.
 */
export function crashKindOf(error: { name?: string; message?: string }, translated: boolean): CrashKind {
  const name = error.name ?? "";
  const message = error.message ?? "";
  // Chrome and Firefox name the call; Safari and jsdom say only that a node
  // "can not be found", which a `NotFoundError` from the DOM always means here.
  const domMismatch =
    /insertBefore|removeChild|not a child of this node/i.test(message) ||
    (name === "NotFoundError" && /can ?not be found/i.test(message));
  if (domMismatch) return "browser_changed_page";
  if (translated && name === "NotFoundError") return "browser_changed_page";
  return "code";
}

export type CrashReport = {
  reference: string;
  kind: CrashKind;
  surface: CrashSurface;
  section?: string | null;
  error: { name?: string; message?: string; digest?: string };
  locale?: string | null;
  translated: boolean;
};

/** A crash loop sends its first reports, not hundreds; one per distinct crash per page load. */
const SENT = new Set<string>();
const SENT_MAX = 5;

/** The exact body the server takes; any other field is refused there. */
export function crashReportBody(report: CrashReport, path: string) {
  const digest = report.error.digest && /^[A-Za-z0-9_-]{1,64}$/.test(report.error.digest) ? report.error.digest : null;
  const build = process.env["NEXT_PUBLIC_BUILD_ID"] ?? "";
  const release = /^[A-Za-z0-9._-]+$/.test(build) ? build.slice(0, 12) : null;
  return {
    reference: report.reference,
    kind: report.kind,
    surface: report.surface,
    section: report.section ?? null,
    name: (report.error.name || "Error").slice(0, 80),
    message: (report.error.message || "").slice(0, 4000),
    digest,
    path: (path.split(/[?#]/, 1)[0] || "/").slice(0, 2000),
    locale: report.locale === "en" || report.locale === "bn" ? report.locale : null,
    translated: report.translated,
    release,
  };
}

/**
 * Posts the report and forgets it. Never throws and never waits: the person is
 * looking at an error screen, and a report that fails to send changes nothing
 * on it.
 */
export function sendCrashReport(report: CrashReport): void {
  if (typeof window === "undefined" || typeof fetch !== "function") return;
  const path = window.location?.pathname ?? "/";
  const key = `${report.surface}:${report.section ?? ""}:${report.error.name ?? ""}:${report.error.message ?? ""}:${path}`;
  if (SENT.has(key) || SENT.size >= SENT_MAX) return;
  SENT.add(key);
  try {
    void fetch(`${getApiBaseUrl()}/v1/client-crashes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(crashReportBody(report, path)),
      credentials: "omit",
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // A browser that refuses the request has refused it; the screen stands.
  }
}

/** For tests: forget what this page load has sent. */
export function resetSentCrashReports(): void {
  SENT.clear();
}

/** What "Copy details" puts on the clipboard: enough for a message to us, nothing more. */
export function crashDetailsText(input: {
  reference: string;
  error: { name?: string; message?: string; digest?: string };
  path: string;
  at: Date;
  userAgent: string;
}): string {
  return [
    `Reference: ${input.reference}`,
    `Page: ${input.path.split(/[?#]/, 1)[0] || "/"}`,
    `Time: ${input.at.toISOString()}`,
    `Error: ${input.error.name || "Error"}: ${input.error.message || ""}`.trim(),
    ...(input.error.digest ? [`Digest: ${input.error.digest}`] : []),
    `Browser: ${input.userAgent}`,
  ].join("\n");
}
